import { useTranslator } from '@bluebeam/react-i18n';
import { SelectOption } from 'components/inputs/BaseSelect';
import MultiplePunchesAssignees from 'components/inputs/dropdowns/AssigneesTabsMultiSelect/MultiplePunchesAssignees';
import Label from 'components/inputs/Label';
import MultiBaseSelect from 'components/inputs/MultiBaseSelect';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import Immutable, { OrderedSet } from 'immutable';
import React, { FC, useEffect, useState } from 'react';
import { Project } from 'records/Project';
import { Punch, PunchStatusValue } from 'records/Punch';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import Assignees from './Assignees';
import DueDate from './DueDate';
import Status from './Status';

type UpdatableFields =
  | 'status'
  | 'duedate'
  | 'assignees'
  | 'disciplines'
  | 'groupAssignees';

const UpdateMultiplePunchOverlay: FC<WithAppStoreProps> = ({
  actions,
  appState,
}) => {
  const { [FeatureFlag.UserGroups]: isUserGroupsEnabled } = useFeatureFlags();
  const { translate } = useTranslator([
    'updateMultiplePunchOverlay',
    'buttons',
    'mapView',
    'disciplineFilterSection',
  ]);
  const [status, setStatus] = useState<PunchStatusValue | null>(null);
  const [duedate, setDuedate] = useState<number>(0);
  const [disciplines, setDisciplines] = useState(
    Immutable.OrderedSet<string>()
  );
  const [assignees, setAssignees] = useState(Immutable.OrderedSet<string>());
  const [groupAssignees, setGroupAssignees] = useState(
    Immutable.OrderedSet<string>()
  );
  const [updatedFields, setUpdatedFields] = useState(
    Immutable.Set<UpdatableFields>()
  );

  const { projects, selectedProject, punches, selectedPunches, projectGroups } =
    appState;
  const project = projects.get(selectedProject, new Project());
  const actualPunches = selectedPunches.map((PunchId: string) =>
    punches.get(PunchId, new Punch())
  );
  useEffect(() => {
    actions.endEditing();
  }, []);

  const handleFieldUpdate = (fieldName: string): void => {
    const field: UpdatableFields = fieldName as UpdatableFields;
    setUpdatedFields((prevUpdatedFields) => prevUpdatedFields.add(field));
    actions.beginEditing();
  };

  const handleUpdateClick = async (): Promise<void> => {
    const updatedPunches = punches
      .filter((i: Punch): boolean => selectedPunches.has(i.id))
      .map((p: Punch): Punch => {
        return updatedFields.reduce((pp: Punch, f: UpdatableFields): Punch => {
          let orderedSetState = null;
          switch (f) {
            case 'assignees':
              orderedSetState = assignees;
              break;
            case 'disciplines':
              orderedSetState = disciplines;
              break;
            case 'groupAssignees':
              orderedSetState = groupAssignees;
              break;
            default:
              orderedSetState = null;
          }
          if (orderedSetState) {
            if (orderedSetState.has('Multiple Assignees')) {
              const a = orderedSetState.delete('Multiple Assignees');
              return pp.updateIn([f], (ppp) => ppp.union(a));
            }
            if (orderedSetState.has('Multiple Groups')) {
              const a = orderedSetState.delete('Multiple Groups');
              return pp.updateIn([f], (ppp) => ppp.union(a));
            }

            if (orderedSetState.has('Multiple Disciplines')) {
              const a = orderedSetState.delete('Multiple Disciplines');
              return pp.updateIn([f], (ppp) => ppp.union(a));
            }
            return pp.set(f, orderedSetState);
          }
          let statusOrDueDateState = null;
          switch (f) {
            case 'status':
              statusOrDueDateState = status;
              break;
            case 'duedate':
              statusOrDueDateState = duedate;
              break;
            default:
              statusOrDueDateState = null;
          }
          if (statusOrDueDateState) {
            return pp.set(f, statusOrDueDateState);
          }
          return pp;
        }, p);
      });
    await actions.updatePunches([...updatedPunches.values()]);
    actions.clearInteractions();
    actions.endEditing();
  };

  const handleCancelClick = (): void => {
    actions.clearInteractions();
    actions.endEditing();
  };

  const updateMultipleAssignees = (
    type: string,
    updatedAssignees: Immutable.OrderedSet<string>
  ) => {
    if (type === 'assignees') {
      setAssignees(updatedAssignees);
    } else if (type === 'groupAssignees') {
      setGroupAssignees(updatedAssignees);
    }
    handleFieldUpdate(type);
  };

  const getCurrentDisciplines = (): SelectOption[] => {
    const hasDisciplines = actualPunches.some((p) => !p.disciplines.isEmpty());

    const disciplineArray: string[] = hasDisciplines
      ? ['Multiple Disciplines']
      : [];

    disciplines.forEach((discipline: string) => {
      if (discipline !== 'Multiple Disciplines') {
        disciplineArray.push(discipline);
      }
    });
    return disciplineArray.map((discipline: string): SelectOption => {
      const translatedDiscipline: `${string}:${string}` =
        `disciplineFilterSection:disciplines.${discipline}` as `${string}:${string}`;
      return {
        label: translate(translatedDiscipline, {
          defaultValue: discipline,
        }),
        value: discipline,
      };
    });
  };

  const disciplinesArray = project.disciplines.toArray();
  const translatedDisciplinesOptions = disciplinesArray.map((discipline) => {
    const translatedDiscipline: `${string}:${string}` =
      `disciplineFilterSection:disciplines.${discipline}` as `${string}:${string}`;
    return {
      value: discipline,
      label: translate(translatedDiscipline, {
        defaultValue: discipline,
      }),
    };
  });

  return (
    <OverlayStdContainer
      id="update-multi-punch-overlay"
      style={{ width: '336px' }}
    >
      <OverlayHeader
        id="update-multi-punch-overlay-header"
        title={translate('updateMultiplePunchOverlay:updateSelectedItems', {
          count: selectedPunches.size,
        })}
        onCancelClick={handleCancelClick}
      />
      {selectedPunches.size > 0 ? (
        <div className="column justify-between os grow-1">
          <div className="p4">
            <Status
              status={status}
              updatedFields={updatedFields}
              project={project}
              actualPunches={actualPunches}
              onChange={(newStatus: PunchStatusValue): void => {
                setStatus(newStatus);
                handleFieldUpdate('status');
              }}
            />
            {project.permissions.createPunch ? (
              <div>
                <br />
                <DueDate
                  duedate={duedate}
                  actualPunches={actualPunches}
                  onChange={(date: Date): void => {
                    const newDate = Math.floor(date.getTime() / 1000);
                    setDuedate(newDate);
                    handleFieldUpdate('duedate');
                  }}
                />
                <br />
                <div className="mb3">
                  <Label
                    label={translate('updateMultiplePunchOverlay:assignees')}
                  />
                  {isUserGroupsEnabled ? (
                    <MultiplePunchesAssignees
                      id="new-punch-overlay-assignee-select"
                      assigneesOptions={project.assignees}
                      groupAssigneesOptions={projectGroups}
                      editable={project.permissions.editAssignee}
                      actualPunches={actualPunches}
                      onChange={(
                        updatedAssignees: Immutable.OrderedSet<string>,
                        type: string
                      ): void => {
                        updateMultipleAssignees(type, updatedAssignees);
                      }}
                    />
                  ) : (
                    <Assignees
                      assignees={assignees}
                      updatedFields={updatedFields}
                      project={project}
                      actualPunches={actualPunches}
                      onChange={(
                        updatedAssignees: Immutable.OrderedSet<string>
                      ): void => {
                        setAssignees(updatedAssignees);
                        handleFieldUpdate('assignees');
                      }}
                    />
                  )}
                </div>
                <Label label={translate('mapView:labels.disciplines')} />
                <MultiBaseSelect
                  id="update-punch-overlay-discipline-select"
                  isMulti
                  value={getCurrentDisciplines()}
                  options={translatedDisciplinesOptions}
                  onChange={(updatedDisciplines: any): void => {
                    const onlyValue = updatedDisciplines.map(
                      (d: SelectOption) => {
                        return d.value;
                      }
                    );
                    setDisciplines(OrderedSet(onlyValue));
                    handleFieldUpdate('disciplines');
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <OverlayFooter
        id="update-multi-punch-overlay-footer"
        confirmText={translate('buttons:update')}
        onCancelClick={handleCancelClick}
        onConfirmClick={handleUpdateClick}
        confirmDisabled={false}
      />
    </OverlayStdContainer>
  );
};

export default withAppStore(UpdateMultiplePunchOverlay);
